import { mergeSxProps } from "@src/appV2/lib";

import { Button, type ButtonProps, type ButtonSize, type ButtonVariant } from "./Button";
import { Icon, type IconSize, type IconType } from "./Icon";

export interface IconButtonProps extends Omit<
  ButtonProps,
  "startIconType" | "endIconType" | "children"
> {
  size?: ButtonSize;
  variant?: ButtonVariant;
  iconType: IconType;
  /**
   * Optional override for the icon size. Defaults to the Icon component's
   * default (`medium`) when not provided.
   */
  iconSize?: IconSize;
  /**
   * When true, render as a circle at any size. Defaults to true for `small`
   * (preserves legacy behavior) and false for `medium`/`large`.
   */
  circular?: boolean;
}

/**
 * An icon-only button that reuses Button variants and sizes.
 * Small size is circular by default; medium/large default to square but can
 * opt in to circular via the `circular` prop.
 */
export function IconButton(props: IconButtonProps) {
  const {
    iconType,
    iconSize,
    size = "medium",
    circular = size === "small",
    sx,
    ...restProps
  } = props;

  return (
    <Button
      {...restProps}
      size={size}
      sx={mergeSxProps((theme) => {
        const buttonHeight = theme.size?.button[size]?.height;
        return {
          minWidth: "unset",
          ...(size === "small" && {
            paddingX: theme.size?.button.small.paddingY,
            paddingY: theme.size?.button.small.paddingY,
          }),
          ...(size === "medium" && {
            paddingX: theme.size?.button.medium.paddingY,
            paddingY: theme.size?.button.medium.paddingY,
          }),
          ...(size === "large" && {
            paddingX: theme.size?.button.large.paddingY,
            paddingY: theme.size?.button.large.paddingY,
          }),
          ...(circular && {
            borderRadius: "50%",
            width: buttonHeight,
            minWidth: buttonHeight,
            aspectRatio: "1",
          }),
        };
      }, sx)}
    >
      <Icon type={iconType} size={iconSize} />
    </Button>
  );
}
